/**
 * Level 3 — one vendor on one project: their contracts, the state of the
 * paperwork behind each, and the two things you do here (add a contract, move
 * one to another vendor).
 */
import { motion } from 'framer-motion';
import { FileText, ChevronRight, Paperclip, ReceiptText, Plus, Move } from 'lucide-react';
import { Panel, fmtINR, fmtDate, RiskPill, StatusPill } from './portfolioUi';

export const LevelVendor = ({ vendor, onOpenContract, onAddContract, onMoveContract }) => (
  <Panel
    title={`${vendor.vendor} — contracts`}
    hint={`${vendor.contracts_count} contracts · ${fmtINR(vendor.value)} committed`}
    testId="level-vendor"
    right={
      <button onClick={onAddContract}
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs"
              style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              data-testid="add-contract-btn">
        <Plus className="w-3.5 h-3.5" /> New contract
      </button>
    }
  >
    <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
      {(vendor.contracts || []).map((c, i) => (
        <motion.div
          key={c.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, delay: Math.min(i, 10) * 0.03 }}
          className="py-4 grid grid-cols-[1fr_auto] gap-6 items-center"
        >
          <button type="button" onClick={() => onOpenContract(c)} className="text-left min-w-0"
                  data-testid={`drilldown-row-contract-${c.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <FileText className="w-4 h-4 shrink-0" style={{ color: 'var(--brand-accent)' }} />
              <span className="text-[11px] tabular-nums" style={{ color: 'var(--text-muted)', fontFamily: '"JetBrains Mono", monospace' }}>
                {c.contract_number}
              </span>
              <span className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{c.title}</span>
              <StatusPill status={c.status} />
              <RiskPill score={c.risk_score} />
            </div>
            <p className="text-[11px] mt-2 flex items-center gap-3 flex-wrap" style={{ color: 'var(--text-muted)' }}>
              <span>{c.type}</span>
              <span className="inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />{c.documents} docs</span>
              <span className="inline-flex items-center gap-1"><ReceiptText className="w-3 h-3" />{c.linked_invoices} linked</span>
              <span>{c.clauses} clauses · {c.obligations} obligations</span>
              <span>{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</span>
            </p>
          </button>
          <div className="text-right shrink-0 flex items-center gap-3">
            <div>
              <p className="text-lg tabular-nums font-light" style={{ color: 'var(--text-primary)', fontFamily: '"JetBrains Mono", monospace' }}>
                {c.value ? fmtINR(c.value) : 'Not set'}
              </p>
              {c.ld_exposure > 0 && <p className="text-[11px] tabular-nums" style={{ color: '#B45309' }}>LD {fmtINR(c.ld_exposure)}</p>}
            </div>
            <button onClick={() => onMoveContract(c)}
                    className="rounded-full border px-2.5 py-1 text-[11px] inline-flex items-center gap-1"
                    style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
                    data-testid={`move-contract-${c.id}`}>
              <Move className="w-3 h-3" /> Move
            </button>
            <ChevronRight className="w-4 h-4" style={{ color: 'var(--text-muted)' }} />
          </div>
        </motion.div>
      ))}
      {!(vendor.contracts || []).length && (
        <p className="text-sm py-8 text-center" style={{ color: 'var(--text-muted)' }} data-testid="vendor-no-contracts">
          No contract with this vendor yet — “New contract” files the first one.
        </p>
      )}
    </div>
  </Panel>
);

export default LevelVendor;
